import { useCallback, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { format, isValid, parse } from 'date-fns'

import {
  parseQueryParams,
  serializeQueryParams,
} from '~publish/hooks/useQueryParam'

import {
  canFulfilComparison,
  DEFAULT_COMPARISON_MODE,
  getComparisonRange,
  parseComparisonMode,
} from '../utils'
import type { ComparisonMode } from '../utils'
import { usePreserveMainScroll } from './usePreserveMainScroll'

interface UseComparisonSelectorOptions {
  primaryStart: Date
  primaryEnd: Date
}

interface ComparisonSelectorResult {
  comparisonMode: ComparisonMode
  comparisonStartDate?: Date
  comparisonEndDate?: Date
  onComparisonModeChange: (mode: ComparisonMode) => void
  onCustomComparisonApply: (start: Date, end: Date) => void
}

const DATE_FORMAT = 'yyyy-MM-dd'

function toScalar(
  value:
    | string
    | string[]
    | null
    | undefined
    | Array<string | string[] | null | undefined>,
): string | undefined {
  if (typeof value === 'string') return value
  if (Array.isArray(value)) {
    const first = value[0]
    return typeof first === 'string' ? first : undefined
  }
  return undefined
}

export function useComparisonSelector({
  primaryStart,
  primaryEnd,
}: UseComparisonSelectorOptions): ComparisonSelectorResult {
  const location = useLocation()
  const navigate = useNavigate()
  const captureScrollBeforeNavigate = usePreserveMainScroll()

  const raw = parseQueryParams(location.search)
  const requestedMode = parseComparisonMode(toScalar(raw.comparison))
  const startParam = toScalar(raw.comparisonStartDate)
  const endParam = toScalar(raw.comparisonEndDate)

  const { comparisonMode, comparisonStartDate, comparisonEndDate } =
    useMemo(() => {
      let customStart: Date | undefined
      let customEnd: Date | undefined
      if (requestedMode === 'custom' && startParam && endParam) {
        const start = parse(startParam, DATE_FORMAT, new Date())
        const end = parse(endParam, DATE_FORMAT, new Date())
        if (isValid(start) && isValid(end) && start <= end) {
          customStart = start
          customEnd = end
        }
      }
      // Fall back to the default whenever the requested mode can't produce a
      // valid equal-length window for this primary range — a custom range that
      // no longer fits (stale/hand-edited URL or a primary-period change), or
      // previous-week/month on too long a period. `canFulfilComparison` is the
      // shared source of truth, also used to warn on reset in
      // `useDateRangeSelector`. For non-custom modes the custom dates are
      // ignored downstream, so passing them through is harmless.
      const effectiveMode: ComparisonMode = canFulfilComparison(
        requestedMode,
        primaryStart,
        primaryEnd,
        customStart,
        customEnd,
      )
        ? requestedMode
        : DEFAULT_COMPARISON_MODE
      const resolved = getComparisonRange(
        effectiveMode,
        primaryStart,
        primaryEnd,
        customStart,
        customEnd,
      )
      return {
        comparisonMode: effectiveMode,
        comparisonStartDate: resolved?.startDate,
        comparisonEndDate: resolved?.endDate,
      }
      // primaryStart/primaryEnd are stable per-render Date instances (from
      // useDateRangeSelector's useMemo), so referential equality is fine here.
    }, [requestedMode, startParam, endParam, primaryStart, primaryEnd])

  const onComparisonModeChange = useCallback(
    (mode: ComparisonMode): void => {
      const params = { ...parseQueryParams(location.search) }
      if (mode !== 'custom') {
        delete params.comparisonStartDate
        delete params.comparisonEndDate
      }
      // Default mode keeps the URL clean — only persist non-default selections.
      if (mode === DEFAULT_COMPARISON_MODE) {
        delete params.comparison
      } else {
        params.comparison = mode
      }
      captureScrollBeforeNavigate()
      navigate({ search: serializeQueryParams(params) }, { replace: true })
    },
    [location.search, navigate, captureScrollBeforeNavigate],
  )

  const onCustomComparisonApply = useCallback(
    (start: Date, end: Date): void => {
      const params = {
        ...parseQueryParams(location.search),
        comparison: 'custom',
        comparisonStartDate: format(start, DATE_FORMAT),
        comparisonEndDate: format(end, DATE_FORMAT),
      }
      captureScrollBeforeNavigate()
      navigate({ search: serializeQueryParams(params) }, { replace: true })
    },
    [location.search, navigate, captureScrollBeforeNavigate],
  )

  return {
    comparisonMode,
    comparisonStartDate,
    comparisonEndDate,
    onComparisonModeChange,
    onCustomComparisonApply,
  }
}
